import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  IconButton,
  Typography,
  Box,
  Alert,
  Divider,
  Stack,
  useMediaQuery,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DocumentIcon from '@mui/icons-material/Description';

import { useAuth } from '../../hooks';
import type { FileUploadType } from '../../features/file-upload';
import { getUserTypeValidity, USER_TYPES } from '../../utils/constants.js';
import { DocumentListItem } from './components';
import { DocumentPreviewModal } from './DocumentPreviewModal.tsx';
import type { DocumentFromStorage, StorageDocuments } from './DocumentManager.types.ts';

const Title = ({ type }: { type: FileUploadType }) => {
  const { userType } = useAuth();

  if (getUserTypeValidity({ userType, validUsers: [USER_TYPES.PATIENT] })) {
    return (
      <Stack direction="column">
        <Stack direction="row" alignItems="center" gap={1}>
          <DocumentIcon />
          <Typography variant="h6">My Documents</Typography>
        </Stack>
        <Typography variant="body2">Documents uploaded by you</Typography>
      </Stack>
    );
  }

  if (type === 'CANDOR_DOCUMENT_UPLOAD') {
    return (
      <Stack direction="column">
        <Stack direction="row" alignItems="center" gap={1}>
          <DocumentIcon />
          <Typography variant="h6">Candor Documents</Typography>
        </Stack>
        <Typography variant="body2">Documents uploaded by Candor</Typography>
      </Stack>
    );
  }

  if (type === 'PATIENT_DOCUMENT_UPLOAD') {
    return (
      <Stack direction="column">
        <Stack direction="row" alignItems="center" gap={1}>
          <DocumentIcon />
          <Typography variant="h6">Patient Documents</Typography>
        </Stack>
        <Typography variant="body2">Documents uploaded by the patient</Typography>
      </Stack>
    );
  }

  throw new Error(`Invalid type: ${type}`);
};

type ContentProps = Pick<DocumentListModalProps, 'isLoading' | 'isError' | 'onDocumentDownload'> & {
  documentList: DocumentFromStorage[];
  onPreview: (document: DocumentFromStorage) => void;
};

const LOADING_LIST = Array.from({ length: 10 }).map((_, index) => ({
  id: `loading-${index}`,
  fileName: 'Loading...',
  description: 'Loading...',
  uploadedAt: new Date(),
  fileSize: 0,
})) as DocumentFromStorage[];

const Content = ({ isLoading, isError, documentList, onDocumentDownload, onPreview }: ContentProps) => {
  if (!isLoading && isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load documents. Please try again.</Alert>
      </Box>
    );
  }

  if (!isLoading && documentList.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <DocumentIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your first document to get started.
        </Typography>
      </Box>
    );
  }

  const list = isLoading ? LOADING_LIST : documentList;

  return (
    <List sx={{ overflow: 'auto' }}>
      {list.map((document, index) => (
        <React.Fragment key={document.id}>
          <DocumentListItem
            isLoading={isLoading}
            document={document}
            onDocumentDownload={onDocumentDownload}
            onPreview={onPreview}
            sx={{
              py: 2,
              px: 3,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          />
          {index < documentList.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </List>
  );
};

interface DocumentListModalProps extends Pick<React.ComponentProps<typeof DocumentPreviewModal>, 'onDocumentDownload'> {
  open: boolean;
  onClose: () => void;
  documents: StorageDocuments;
  isLoading: boolean;
  isError: boolean;
  type: FileUploadType;
}

export const DocumentListModal: React.FC<DocumentListModalProps> = ({
  open,
  onClose,
  documents,
  isLoading,
  isError,
  onDocumentDownload,
  type,
}) => {
  const [previewDocument, setPreviewDocument] = useState<DocumentFromStorage | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const isSmallScreen = useMediaQuery('(max-width: 320px)');

  const documentList = useMemo(
    () =>
      Object.values(documents).sort((a, b) => {
        if (!a.uploadedAt || !b.uploadedAt) {
          return 0;
        }

        return b.uploadedAt.getTime() - a.uploadedAt.getTime();
      }),
    [documents],
  );

  const handlePreview = (document: DocumentFromStorage) => {
    setPreviewDocument(document);
    setIsPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
    setPreviewDocument(null);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isSmallScreen}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Title type={type} />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Content
            isLoading={isLoading}
            isError={isError}
            documentList={documentList}
            onDocumentDownload={onDocumentDownload}
            onPreview={handlePreview}
          />
        </DialogContent>
      </Dialog>

      <DocumentPreviewModal
        open={isPreviewOpen}
        onClosePreview={handleClosePreview}
        document={previewDocument}
        onDocumentDownload={onDocumentDownload}
      />
    </>
  );
};
